// Studio table-explorer UI for managed PostgreSQL deployments.
//
// Wraps @prisma/studio-core in the same modal pattern as SqlEditorSection.
// On open: POST /studio/session → spin up the ephemeral BFF pod in the
// tenant namespace, get a session_id.
// On close: DELETE /studio/session/{session_id} (idempotent — the BFF
// also self-shutdowns after 15 min idle).
//
// Studio sends Prisma protocol payloads {procedure, query|sequence} via
// our axios client (cookies = same auth as the rest of the app) to the
// /studio/proxy/{session_id} endpoint, which forwards through the K8s
// API server to the BFF pod.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';
import Logger from '../utils/logger';
import { getApiErrorMessage } from '../constants/messages';
import { Studio } from '@prisma/studio-core/ui';
import { createPostgresAdapter } from '@prisma/studio-core/data/postgres-core';
import '@prisma/studio-core/ui/index.css';

interface StudioSectionProps {
  deploymentId: string;
}

const StudioSection: React.FC<StudioSectionProps> = ({ deploymentId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [openingSession, setOpeningSession] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [databases, setDatabases] = useState<Array<{name: string; is_default: boolean; has_dedicated_user: boolean; user?: string}>>([]);
  // Vide tant que la liste server-side n'a pas repondu. Le seul nom de base
  // valide vient de listSqlEditorDatabases (resolu via le CRD Cluster CNPG).
  // Un prop `databaseName` alimente avec application_name servait de valeur
  // initiale : la session s'ouvrait sur "savana-agency" au lieu de
  // "agentBanking" -> 400 "not authorized for this deployment". Prop supprime.
  const [selectedDatabase, setSelectedDatabase] = useState<string>('');
  const [databasesError, setDatabasesError] = useState<string | null>(null);

  // Charger la liste des DBs au 1er open (idem SqlEditorSection). Permet le
  // dropdown de sélection et de passer le param `database` à openStudioSession.
  useEffect(() => {
    if (!isModalOpen || databases.length > 0) return;
    apiService.listSqlEditorDatabases(deploymentId)
      .then((res) => {
        setDatabases(res.databases);
        const def = res.databases.find((d) => d.is_default);
        setSelectedDatabase(def?.name || res.databases[0]?.name || '');
      })
      .catch((err) => {
        // Sans la liste, aucun nom de base valide : on n'ouvre pas de session
        // plutot que d'en tenter une qui sera refusee par l'allowlist.
        Logger.error('Erreur chargement DBs Studio', err);
        setDatabasesError(getApiErrorMessage(err, 'Impossible de charger la liste des bases'));
      });
  }, [isModalOpen, deploymentId, databases.length]);

  // Custom executor: routes through apiService.studioProxyRequest so cookies
  // (auth) + 401 interceptor work. Wraps the Studio adapter args in the BFF
  // wire format {procedure, query|sequence} — same shape that
  // createStudioBFFClient produces internally.
  //
  // Both `execute` and `executeSequence` must exist — Studio calls
  // whichever based on whether it's a single query or a paginated/count
  // pair. Missing one would silently throw "not a function" and break
  // sections of the UI without showing a clear error.
  const adapter = useMemo(() => {
    if (!sessionId) return null;
    const executor = {
      execute: async (query: unknown, _options?: unknown): Promise<unknown> => {
        try {
          const body = await apiService.studioProxyRequest(
            deploymentId,
            sessionId,
            { procedure: 'query', query }
          );
          return body;
        } catch (err: unknown) {
          const message = getApiErrorMessage(err, 'Studio proxy error');
          return [{ name: 'Error', message }];
        }
      },
      executeSequence: async (sequence: unknown, _options?: unknown): Promise<unknown> => {
        try {
          const body = await apiService.studioProxyRequest(
            deploymentId,
            sessionId,
            { procedure: 'sequence', sequence }
          );
          return body;
        } catch (err: unknown) {
          const message = getApiErrorMessage(err, 'Studio proxy error');
          return [[{ name: 'Error', message }]];
        }
      },
    };
    return createPostgresAdapter({ executor: executor as never });
  }, [deploymentId, sessionId]);

  const openSession = useCallback(async () => {
    setOpeningSession(true);
    setSessionError(null);
    try {
      const res = await apiService.openStudioSession(deploymentId, selectedDatabase);
      setSessionId(res.session_id);
    } catch (err: unknown) {
      Logger.error('Failed to open Studio session', err);
      const message = getApiErrorMessage(err, 'Cannot open Studio session');
      setSessionError(message);
      toast.error(message);
    } finally {
      setOpeningSession(false);
    }
  }, [deploymentId, selectedDatabase]);

  // Switch de DB : fermer la session courante et reset — le useEffect suivant
  // en ouvrira une nouvelle automatiquement pour la nouvelle DB.
  const handleDatabaseChange = useCallback(async (newDb: string) => {
    if (newDb === selectedDatabase) return;
    if (sessionId) {
      apiService.closeStudioSession(deploymentId, sessionId).catch(() => undefined);
      setSessionId(null);
    }
    setSelectedDatabase(newDb);
  }, [deploymentId, sessionId, selectedDatabase]);

  const closeSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      await apiService.closeStudioSession(deploymentId, sessionId);
    } catch (err) {
      // Idempotent on the backend; if it fails, BFF idle-shutdown will reap it.
      Logger.error('Failed to close Studio session (ignored)', err);
    } finally {
      setSessionId(null);
    }
  }, [deploymentId, sessionId]);

  // Open the session when the modal opens — mais SEULEMENT une fois la base
  // cible resolue par le chargement de la liste. Sans le garde
  // `selectedDatabase`, cet effet partait en meme temps que le fetch de la
  // liste et gagnait la course, ouvrant la session sur la valeur initiale.
  useEffect(() => {
    if (isModalOpen && selectedDatabase && !sessionId && !openingSession) {
      openSession();
    }
  }, [isModalOpen, selectedDatabase, sessionId, openingSession, openSession]);

  // Le retry doit viser l'etape qui a echoue : sans liste de bases, rouvrir une
  // session est voue a l'echec (aucun nom valide a envoyer).
  const retry = useCallback(() => {
    if (databasesError) {
      setDatabasesError(null);
      setDatabases([]);   // relance le useEffect de chargement
      return;
    }
    openSession();
  }, [databasesError, openSession]);

  // Close session on unmount (e.g. user navigates away while modal is open).
  useEffect(() => {
    return () => {
      if (sessionId) {
        apiService.closeStudioSession(deploymentId, sessionId).catch(() => undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    closeSession();
  };

  return (
    <>
      {/* Trigger button — same visual style as SqlEditorSection */}
      <button
        onClick={() => setIsModalOpen(true)}
        className="w-full bg-surface-2 shadow-card rounded-card px-4 py-4 flex items-center justify-between hover:bg-surface-3 transition-colors group"
      >
        <div className="flex items-center space-x-3">
          <svg
            className="h-6 w-6 text-text-tertiary group-hover:text-accent-500 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
          <span className="text-sm font-medium text-text-primary">Studio (table explorer)</span>
        </div>
        <svg
          className="h-5 w-5 text-text-tertiary group-hover:text-text-secondary transition-colors"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Modal — full-bleed because Studio needs the room */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="flex items-center justify-center min-h-screen">
            <div className="fixed inset-0 bg-black/70 transition-opacity" onClick={handleCloseModal} />

            <div className="relative bg-surface-1 rounded-xl shadow-2xl w-[95vw] h-[92vh] flex flex-col">
              {/* Header — same padding scale as SqlEditorSection for consistency */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                  <h2 className="text-lg font-semibold text-text-primary">Studio</h2>
                  {databases.length > 1 ? (
                    <select
                      value={selectedDatabase}
                      onChange={(e) => handleDatabaseChange(e.target.value)}
                      disabled={openingSession}
                      className="text-sm bg-surface-3 text-text-primary border border-border-primary rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-accent-500 disabled:opacity-50"
                    >
                      {databases.map((d) => (
                        <option key={d.name} value={d.name}>
                          {d.name}{d.is_default ? ' (default)' : ''}{d.has_dedicated_user ? ` — user: ${d.user}` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-sm text-text-tertiary truncate">— {selectedDatabase || '…'}</span>
                  )}
                  {sessionId && (
                    <span className="flex items-center gap-2 text-xs text-green-400">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      session active
                    </span>
                  )}
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-text-tertiary hover:text-text-primary transition-colors"
                  aria-label="Close Studio"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body — Studio takes all remaining height */}
              <div className="flex-1 overflow-hidden bg-background">
                {openingSession && (
                  <div className="h-full flex items-center justify-center text-text-secondary">
                    <div className="text-center">
                      <svg className="animate-spin h-10 w-10 text-accent-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <p className="text-sm">Démarrage de la session Studio…</p>
                      <p className="text-xs text-text-tertiary mt-1">le premier boot prend ~5 s</p>
                    </div>
                  </div>
                )}

                {(sessionError || databasesError) && !openingSession && (
                  <div className="h-full flex items-center justify-center px-6">
                    <div className="text-center max-w-md">
                      <div className="w-12 h-12 bg-red-500/15 rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg className="h-6 w-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-red-400 mb-2">
                        {databasesError ? 'Impossible de charger les bases' : "Impossible d'ouvrir la session"}
                      </p>
                      <p className="text-xs text-text-tertiary">{databasesError || sessionError}</p>
                      <button
                        onClick={retry}
                        className="mt-4 inline-flex items-center px-4 py-2 bg-accent-600 text-white text-sm font-medium rounded-lg hover:bg-accent-700 transition-colors"
                      >
                        Réessayer
                      </button>
                    </div>
                  </div>
                )}

                {adapter && !openingSession && !sessionError && (
                  <div className="h-full w-full">
                    <Studio adapter={adapter} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StudioSection;
